import Cookies from 'js-cookie';

import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemTypeEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import {
  COOKIE_COMPASSION_DOMAIN,
  COOKIE_NAME_CAMPAIGN_LE,
} from '@sola/shared-utils/constants.ts';

const isAfterMidnightLocal = (timestamp: number): boolean => {
  if (
    Number.isNaN(timestamp) ||
    !Number.isFinite(timestamp) ||
    timestamp <= 0
  ) {
    // Invalid timestamp, return true to remove cookie
    return true;
  }

  const ts = new Date(timestamp);

  // Get the local values from timestamp
  const tsLocalYear = ts.getFullYear();
  const tsLocalMonth = ts.getMonth();
  // Get the tomorrow's day of month for local
  const tsLocalDate = ts.getDate() + 1;

  // Create a Date that represents **00:00:00 local** of that next day
  const tsLocalMidnight = new Date(tsLocalYear, tsLocalMonth, tsLocalDate);

  // Could use .getTime() + (hours past midnight) * 60 * 60 * 1000
  return Date.now() >= tsLocalMidnight.getTime();
};

export const isActiveLiveEvent = () => {
  // Read the cookie
  const leCookie = Cookies.get(COOKIE_NAME_CAMPAIGN_LE);

  if (!leCookie) {
    return false;
  }

  const cookieTimestamp = Number.parseInt(leCookie, 10);

  if (isAfterMidnightLocal(cookieTimestamp)) {
    // Remove the cookie if it's from a previous day
    Cookies.remove(COOKIE_NAME_CAMPAIGN_LE, {
      domain:
        process.env.NEXT_PUBLIC_COOKIE_DOMAIN_STAGE || COOKIE_COMPASSION_DOMAIN,
    });
    return false;
  }

  return true;
};

export const suppressForLiveEvent = (cart?: Cart | null) => {
  if (!cart?.campaign?.suppressAddonsUpsells) {
    // Get out
    return false;
  }
  return isActiveLiveEvent();
};

export const displaySocialProofAnonymousOption = (
  cart?: Cart | null,
): boolean => {
  return !!cart?.campaign?.showPrivacyOption && isActiveLiveEvent();
};

/**
 * A sponsorship item has the introductory (reduced) dual rate selected when its
 * current unit price matches the campaign's CDSP override rate. When dual rate
 * is offered, the override rate is always the reduced intro rate, so a matching
 * unit price means the supporter chose the intro rate over the standard rate.
 * Only sponsorship items participate in dual-rate selection.
 */
export const isIntroRateSelected = (
  item: CartItem,
  cdspOverrideRate?: number,
): boolean =>
  item.itemType === CartItemTypeEnum.Sponsorship &&
  cdspOverrideRate !== undefined &&
  item.unitPrice === cdspOverrideRate;
